import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";

// Glitch animation for the redacted box when hovered
const glitchAnim = keyframes`
  0% { transform: translate(0) }
  20% { transform: translate(-2px, 2px) }
  40% { transform: translate(-2px, -2px) }
  60% { transform: translate(2px, 2px) }
  80% { transform: translate(2px, -2px) }
  100% { transform: translate(0) }
`;

const ClassifiedWrapper = styled.span`
  display: inline-block;
  position: relative;
  cursor: crosshair;
  padding: 0 2px;
  line-height: inherit;
  vertical-align: baseline;
`;

const RedactedBlock = styled.span`
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 0;
  width: 100%;
  border-radius: 2px;
  background-color: ${props => props.$isRevealed ? 'transparent' : '#fff'};
  color: ${props => props.$isRevealed ? 'transparent' : '#fff'};
  transition: background-color 0.1s;
  pointer-events: none; /* Let hovers pass through to wrapper */
  z-index: 2;

  ${props => !props.$isRevealed && css`
    &:hover {
      animation: ${glitchAnim} 0.2s infinite;
      opacity: 0.8;
    }
  `}
`;

const DecodedTextContainer = styled.span`
  display: inline-block;
  color: #04e5e5; /* Cyberpunk cyan when revealed */
  opacity: ${props => props.$isRevealed ? 1 : 0};
  transition: opacity 0.1s;
  position: relative;
  z-index: 1;
`;

const HiddenOriginalText = styled.span`
  visibility: hidden;
  display: inline;
`;

const BaffleAbsolute = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  white-space: nowrap;
`;


const ClassifiedText = ({ children, onReveal }) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const textRef = useRef(null);
  const textString = typeof children === 'string' ? children : String(children);

  const handleMouseEnter = () => {
    if (isRevealed) return;
    setIsRevealed(true);
    if (onReveal) setTimeout(onReveal, 0);
  };

  useEffect(() => {
    if (!isRevealed || !textRef.current) return;
    
    const chars = "█▓▒░/^!@#$%^&*()_+-=[]{}|;':,.<>?";
    const duration = 1000; 
    const stepTime = 50; 
    let elapsed = 0;
    
    // Fast, React-bypassing DOM mutation loop
    const interval = setInterval(() => {
        elapsed += stepTime;
        const progress = Math.min(elapsed / duration, 1);
        
        let scrambled = "";
        for (let i = 0; i < textString.length; i++) {
            // Once progress reaches near 1, more characters settle to original
            if (Math.random() < progress) {
                scrambled += textString[i];
            } else {
                scrambled += chars[Math.floor(Math.random() * chars.length)];
            }
        }
        
        if (textRef.current) {
            textRef.current.innerText = scrambled;
        }

        if (progress === 1) {
            clearInterval(interval);
            if (textRef.current) textRef.current.innerText = textString;
        }
    }, stepTime);

    return () => clearInterval(interval);
  }, [isRevealed, textString]);

  return (
    <ClassifiedWrapper 
      onMouseEnter={handleMouseEnter} 
      onClick={handleMouseEnter}
      onTouchStart={handleMouseEnter}
      title={isRevealed ? "Decrypted" : "RESTRICTED ACCESS"}
    >
      <RedactedBlock $isRevealed={isRevealed} />
      
      <DecodedTextContainer $isRevealed={isRevealed}>
        <HiddenOriginalText>{textString}</HiddenOriginalText>
        {isRevealed && (
            <BaffleAbsolute ref={textRef}>
                {textString}
            </BaffleAbsolute>
        )}
      </DecodedTextContainer>
    </ClassifiedWrapper>
  );
};

export default React.memo(ClassifiedText);
